import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { SmsMessage } from '../../../models/smsMessage'
import { Workgroup } from '../../../models/workgroup'
import { AlertController, IonicModule, IonInfiniteScroll, IonSelect } from '@ionic/angular'
import { UsersService } from '../../../services/domain/users.service'
import { first, map, shareReplay, takeUntil, tap } from 'rxjs/operators'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { FeathersHealthCheckService } from '../../../services/feathersHealthCheck/feathers-health-check.service'
import { NgxFloatUiContentComponent, NgxFloatUiModule, NgxFloatUiTriggers } from 'ngx-float-ui'
import { StripHtmlPipe } from '../../../shared/pipes/strip-html.pipe'
import { AsyncPipe, CommonModule, DatePipe } from '@angular/common'
import { TooltipModule } from 'primeng/tooltip'

type ActiveTab = 'inbound' | 'outbound'

@Component({
  selector: 'app-sms-messages-list',
  standalone: true,
  imports: [StripHtmlPipe, CommonModule, IonicModule, NgxFloatUiModule, DatePipe, AsyncPipe, TooltipModule],
  templateUrl: './sms-messages-list.component.html',
  styleUrls: ['./sms-messages-list.component.scss'],
})
export class SmsMessagesListComponent implements OnInit, OnDestroy, OnChanges {
  private alertController = inject(AlertController)
  private userService = inject(UsersService)
  private healthCheckService = inject(FeathersHealthCheckService)

  // @Input() messages: SmsMessage[]

  @Input() inboundMessages$: Observable<SmsMessage[]>
  @Input() outboundMessages$: Observable<SmsMessage[]>
  @Input() workgroups: Workgroup[]

  @Output() workgroupFilterChanged = new EventEmitter<Workgroup>()
  @Output() loadInboundSmsFromOffset = new EventEmitter<number>()
  @Output() loadOutboundSmsFromOffset = new EventEmitter<number>()
  @Output() messageRead = new EventEmitter<SmsMessage>()
  @Output() markAllMessagesRead = new EventEmitter<void>()

  @ViewChild('inboundInfiniteScroll') inboundInfiniteScroll: IonInfiniteScroll
  @ViewChild('outboundInfiniteScroll') outboundInfiniteScroll: IonInfiniteScroll
  @ViewChild('workgroupFilterSelect') wgSelect: IonSelect
  @ViewChild('markAllAsReadPopper') markAllAsReadPopper: NgxFloatUiContentComponent

  smsHubConnected$: Observable<boolean>

  private sortedInboundMessagesSubject$ = new BehaviorSubject<SmsMessage[]>([])
  sortedInboundMessages$ = this.sortedInboundMessagesSubject$.asObservable()

  sortedOutboundMessages$: Observable<SmsMessage[]>

  private inboundMessagesLoadedLength: number = 0
  private outboundMessagesLoadedLength: number = 0

  private destroy$ = new Subject<void>()

  defaultWorkgroupValue: any
  compareWithFn = (o1, o2) => (o1 && o2 ? o1.id === o2.id : o1 === o2)

  activeTab: ActiveTab = 'inbound'
  protected readonly NgxFloatUiTriggers = NgxFloatUiTriggers

  constructor() {
    this.smsHubConnected$ = this.healthCheckService.checkSmsHubHealth()
  }

  ngOnInit() {
    this.inboundMessages$
      .pipe(
        tap((msgs) => console.log('new inbound msg', msgs)),
        map((msgs) => msgs.sort(this.sortByCreatedAt)),
        tap((msgs) => {
          this.inboundMessagesLoadedLength = msgs.length
          this.sortedInboundMessagesSubject$.next(msgs)
          this.inboundInfiniteScroll?.complete()
        }),
        takeUntil(this.destroy$),
        shareReplay({ bufferSize: 1, refCount: true }),
      )
      .subscribe()

    this.sortedOutboundMessages$ = this.outboundMessages$.pipe(
      tap((msgs) => console.log('new outbound msg', msgs)),
      map((msgs) => msgs.sort(this.sortByCreatedAt)),
      tap((msgs) => {
        this.outboundMessagesLoadedLength = msgs.length
        this.outboundInfiniteScroll?.complete()
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.loadMoreInboundData()
    this.loadMoreOutboundData()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  sortByCreatedAt = (a: SmsMessage, b: SmsMessage) => {
    if (a.createdAt < b.createdAt) return 1
    if (a.createdAt > b.createdAt) return -1
    return 0
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log(changes)
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        switch (propName) {
          // case 'messages':
          //   if (!this.messages) break
          //   this.sortedInboundMessages = this.messages.sort((a, b) => {
          //     if (a.createdAt < b.createdAt) return 1
          //     if (a.createdAt > b.createdAt) return -1
          //     return 0
          //   } )
          //   this.inboundInfiniteScroll.complete()
          //   break
          case 'workgroups':
            this.userService
              .getCurrentUserWorkgroup()
              .pipe(first(), takeUntil(this.destroy$))
              .toPromise()
              .then((wg) => {
                console.log('sms select got wg', wg)
                this.defaultWorkgroupValue = wg
              })
        }
      }
    }
  }

  workgroupIdFilterSelectChanged($event: any) {
    const wg: Workgroup = $event.target.value
    this.workgroupFilterChanged.emit(wg)
  }

  async openMessage(msg: SmsMessage) {
    const fromAndContent = `Od: ${msg.getName()}<br>${msg.body}`

    const alert = await this.alertController.create({
      cssClass: 'privateMessageAlert',
      header: msg.getName(),
      message: fromAndContent,
      buttons: [
        { text: 'Zpět', role: 'cancel', cssClass: 'secondary', handler: (blah) => console.log('Zpět', blah) },
        { text: 'Odpovědět', handler: () => console.log('Odpovědět') },
      ],
    })

    await alert.present()
    if (msg.smsDirection === 'in') {
      this.markMessageRead(msg)
    }
  }

  loadMoreInboundData() {
    console.log('loading more inbound sms...')
    this.loadInboundSmsFromOffset.emit(this.inboundMessagesLoadedLength)
  }

  loadMoreOutboundData() {
    console.log('loading more outbound sms...')
    this.loadOutboundSmsFromOffset.emit(this.outboundMessagesLoadedLength)
  }

  private markMessageRead(msg: SmsMessage) {
    msg.setReadByCurrentUser(true)
    this.messageRead.emit(msg)
    this.sortedInboundMessagesSubject$.next([...this.sortedInboundMessagesSubject$.value])
  }

  markAllAsRead() {
    const allInbound = this.sortedInboundMessagesSubject$.value.map((m) => m.setReadByCurrentUser(true))
    this.sortedInboundMessagesSubject$.next([...allInbound])

    this.markAllMessagesRead.emit()
  }

  activeTabChange(event: CustomEvent) {
    this.activeTab = event.detail.value
  }
}
